import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Avatar,
  Collapse,
  IconButton,
  Divider,
  Button
} from "@material-ui/core";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from "@material-ui/lab";
import {
  AddCircle,
  Edit,
  Link,
  SwapHoriz,
  PersonAdd,
  ExpandMore,
  ExpandLess,
  History,
  Schedule,
  Person
} from "@material-ui/icons";
import {
  TICKET_HISTORY_CONFIG,
  getChangeTypeColor,
  getChangeTypeDisplayName
} from "./ticketHistoryConfig";

const TicketHistoryComponent = ({ history = [], ticketId }) => {
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [showAllHistory, setShowAllHistory] = useState(true);

  const getChangeTypeIcon = (changeType) => {
    const iconMap = {
      created: AddCircle,
      updated: Edit,
      mapped: Link,
      status_changed: SwapHoriz,
      assigned: PersonAdd
    };
    return iconMap[changeType] || Edit;
  };

  const toggleExpanded = (index) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    // If less than 24 hours, show relative time
    if (diffInHours < 24) {
      if (diffInHours < 1) {
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));
        return diffInMinutes < 1 ? 'Just now' : `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
      }
      return `${Math.floor(diffInHours)} hour${Math.floor(diffInHours) > 1 ? 's' : ''} ago`;
    }
    
    // Otherwise show full date and time
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }) + ' ' + date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFullDateTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Sort history by timestamp (newest first)
  const sortedHistory = [...history].sort((a, b) => 
    new Date(b.timestamp) - new Date(a.timestamp)
  );

  const renderHistoryItem = (item, index) => {
    const IconComponent = getChangeTypeIcon(item.changeType);
    const color = getChangeTypeColor(item.changeType);
    const isExpanded = expandedItems.has(index);
    const hasDetails = item.oldValue !== item.newValue;
    const isLast = index === sortedHistory.length - 1;

    return (
      <TimelineItem key={index}>
        <TimelineOppositeContent style={{ minWidth: 120 }}>
          <Box display="flex" flexDirection="column" alignItems="flex-end">
            <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.7rem', fontWeight: 500 }}>
              {formatDateTime(item.timestamp)}
            </Typography>
            <Box display="flex" alignItems="center" marginTop={0.5}>
              <Schedule style={{ fontSize: 12, marginRight: 2, color: '#666' }} />
              <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.6rem' }}>
                {new Date(item.timestamp).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>
            </Box>
          </Box>
        </TimelineOppositeContent>
        <TimelineSeparator>
          <TimelineDot style={{ backgroundColor: color, width: 32, height: 32 }}>
            <IconComponent style={{ color: 'white', fontSize: 16 }} />
          </TimelineDot>
          {!isLast && <TimelineConnector style={{ backgroundColor: color, opacity: 0.3 }} />}
        </TimelineSeparator>
        <TimelineContent>
          <Card style={{ 
            marginBottom: 8, 
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: `1px solid ${color}20`,
            borderRadius: 8
          }}>
            <CardContent style={{ padding: 12 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box flex={1}>
                  <Box display="flex" alignItems="center" marginBottom={1}>
                    <Chip
                      label={getChangeTypeDisplayName(item.changeType)}
                      size="small"
                      style={{ 
                        backgroundColor: color, 
                        color: 'white', 
                        marginRight: 8, 
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        height: 20
                      }}
                    />
                    <Typography variant="body2" style={{ fontWeight: 600, fontSize: '0.85rem', color: '#333' }}>
                      {item.fieldDisplayName}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" alignItems="center" marginBottom={1}>
                    <Person style={{ fontSize: 14, marginRight: 4, color: '#666' }} />
                    <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.75rem' }}>
                      {item.user.username || item.user.email}
                    </Typography>
                  </Box>

                  {hasDetails ? (
                    <Box style={{ backgroundColor: '#f8f9fa', padding: 8, borderRadius: 4, border: '1px solid #e9ecef' }}>
                      <Box display="flex" alignItems="center" marginBottom={0.5}>
                        <Typography variant="caption" style={{ fontWeight: 600, fontSize: '0.7rem', color: '#dc3545' }}>
                          From:
                        </Typography>
                        <Typography variant="caption" style={{ marginLeft: 4, fontSize: '0.7rem', color: '#666' }}>
                          {item.oldValue || 'Not set'}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center">
                        <Typography variant="caption" style={{ fontWeight: 600, fontSize: '0.7rem', color: '#28a745' }}>
                          To:
                        </Typography>
                        <Typography variant="caption" style={{ marginLeft: 4, fontSize: '0.7rem', color: '#666' }}>
                          {item.newValue || 'Not set'}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box style={{ backgroundColor: '#e8f5e8', padding: 8, borderRadius: 4, border: '1px solid #c3e6c3' }}>
                      <Typography variant="caption" style={{ fontSize: '0.75rem', color: '#155724' }}>
                        <strong>Value set to:</strong> {item.newValue || 'Not set'}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {hasDetails && (
                  <IconButton
                    size="small"
                    onClick={() => toggleExpanded(index)}
                    style={{ marginLeft: 8, padding: 4 }}
                  >
                    {isExpanded ? <ExpandLess style={{ fontSize: 16 }} /> : <ExpandMore style={{ fontSize: 16 }} />}
                  </IconButton>
                )}
              </Box>

              {isExpanded && hasDetails && (
                <Collapse in={isExpanded}>
                  <Divider style={{ margin: '8px 0' }} />
                  <Box padding={1} style={{ backgroundColor: '#f8f9fa', borderRadius: 4, border: '1px solid #dee2e6' }}>
                    <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.65rem', fontWeight: 600 }}>
                      Detailed Change Information:
                    </Typography>
                    <Box marginTop={0.5}>
                      <Typography variant="caption" style={{ fontSize: '0.65rem', color: '#666' }}>
                        <strong>Previous Value:</strong> {item.oldValue || 'Not set'}
                      </Typography>
                    </Box>
                    <Box marginTop={0.5}>
                      <Typography variant="caption" style={{ fontSize: '0.65rem', color: '#666' }}>
                        <strong>New Value:</strong> {item.newValue || 'Not set'}
                      </Typography>
                    </Box>
                    <Box marginTop={0.5}>
                      <Typography variant="caption" style={{ fontSize: '0.65rem', color: '#666' }}>
                        <strong>Changed At:</strong> {formatFullDateTime(item.timestamp)}
                      </Typography>
                    </Box>
                  </Box>
                </Collapse>
              )}
            </CardContent>
          </Card>
        </TimelineContent>
      </TimelineItem>
    );
  };

  if (!history || history.length === 0) {
    return (
      <Card style={{ marginTop: 16, boxShadow: '0 2px 8px rgba(33, 150, 243, 0.07)' }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="center" padding={3}>
            <History style={{ marginRight: 8, color: '#ccc' }} />
            <Typography variant="body1" color="textSecondary">
              No history available for this ticket
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const displayHistory = showAllHistory 
    ? sortedHistory 
    : sortedHistory.slice(0, TICKET_HISTORY_CONFIG.display.maxHistoryItems);

  return (
    <Card style={{ height: '100%', boxShadow: '0 2px 8px rgba(33, 150, 243, 0.07)' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={2}>
          <Box display="flex" alignItems="center">
            <History style={{ marginRight: 8, color: '#1976d2' }} />
            <Typography variant="h6" style={{ fontWeight: 600, color: '#1976d2' }}>
              Ticket Timeline ({history.length} changes)
            </Typography>
          </Box>
          {history.length > TICKET_HISTORY_CONFIG.display.maxHistoryItems && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => setShowAllHistory(!showAllHistory)}
              style={{ fontSize: '0.75rem' }}
            >
              {showAllHistory ? "Show Recent" : `Show All ${history.length}`}
            </Button>
          )}
        </Box>

        <Box style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto', paddingRight: 8 }}>
          <Timeline align="left" style={{ padding: 0 }}>
            {displayHistory.map((item, index) => renderHistoryItem(item, index))}
          </Timeline>
          
          {!showAllHistory && history.length > TICKET_HISTORY_CONFIG.display.maxHistoryItems && (
            <Box textAlign="center" marginTop={2} padding={2}>
              <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.75rem' }}>
                Showing {TICKET_HISTORY_CONFIG.display.maxHistoryItems} of {history.length} changes
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default TicketHistoryComponent; 